import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { Box, Button, Skeleton, IconButton, Menu, MenuItem, FormControl, InputLabel } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useQuery } from 'react-query'
import { TextInput, useListContext, useTranslate, BooleanInput, DateTimeInput } from 'react-admin'
import ContentFilter from '@mui/icons-material/FilterList'
import isEqual from 'lodash/isEqual'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { HighlightOff } from '@mui/icons-material'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'
import { promotionServices } from 'src/services/reservation/promotion/promotion.service'
import { TreeViewInputOperation } from './logic/TreeViewWorkUnitOperation'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import { gatewayServices } from 'src/services/payment/gateway/gateway.service'

export const PostFilterButtonOperation = ({ listFilters }) => {
  const { displayedFilters = {}, setFilters, showFilter } = useListContext()
  const translate = useTranslate()
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()

  const handleClickButton = useCallback(
    (event) => {
      // This prevents ghost click.
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        size='small'
      >
        <ContentFilter /> {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters({}, {}, false)}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

const SelectArrayInputFn = ({
  queryName,
  label,
  optionsText = ['long_name'],
  optionValue,
  source,
  queryFn,
  ...props
}) => {
  const options = useQuery(queryName, () => queryFn())
  return options.isLoading ? (
    <Skeleton variant='rectangular' width={'270px'} height={'40px'} />
  ) : (
    <FormControl fullWidth className='select-array-input'>
      {label && <InputLabel>{label}</InputLabel>}
      <SelectInput
        source={source}
        options={options?.data || []}
        optionsText={optionsText}
        optionValue={optionValue}
        multiple
        showSearch
        {...props}
      />
    </FormControl>
  )
}

const PromotionFilterInput = ({ source, label }) => {
  const translate = useTranslate()
  const options = useQuery('promotion-all', () => promotionServices.getByAll({ limit: 999 }))
  
  const formatOptions = () => {
    if (!options.data) return []
    const allOption = { id: 'ALL', name: translate('ra.action.all_promotions') }
    const promotionOptions = options.data.map(promo => ({
      id: promo.name,
      name: promo.name
    }))
    return [allOption, ...promotionOptions]
  }

  const parseValue = (value) => {
    if (!value || value.length === 0) return undefined
    if (value.includes('ALL')) return 'ALL'
    return value.join(',')
  }

  const formatValue = (value) => {
    if (!value) return []
    if (value === 'ALL') return ['ALL']
    return value.split(',')
  }

  return options.isLoading ? (
    <Skeleton variant='rectangular' width={'270px'} height={'40px'} />
  ) : (
    <FormControl fullWidth className='select-array-input'>
      {label && <InputLabel>{label}</InputLabel>}
      <SelectInput
        source={source}
        options={formatOptions()}
        optionsText={['name']}
        optionValue='id'
        multiple
        showSearch
        format={formatValue}
        parse={parseValue}
      />
    </FormControl>
  )
}

const RemoveFilterField = ({ children, prop, fields, width = null, maxWidth = null, minWidth = null }) => {
  const { displayedFilters = {}, setFilters } = useListContext()
  const { setValue, getValues } = useFormContext()

  const onClickButton = () => {
    const copy = { ...displayedFilters }
    delete copy[prop]

    if (Array.isArray(fields) && fields.length > 0) {
      fields.forEach((field) => setValue(field, undefined))

      let values = getValues()
      setFilters(values, copy, false)
    }
  }

  return (
    <Box
      display={'flex'}
      alignItems={'flex-start'}
      marginRight={5}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      <Box width={'34px'} paddingTop={1}>
        <IconButton size='small' onClick={onClickButton}>
          <HighlightOff />
        </IconButton>
      </Box>
      <Box width={'calc(100% - 34px)'} display={'flex'} minHeight={'60px'} height={'auto'}>
        {children}
      </Box>
    </Box>
  )
}

export const PostFilterFormOperation = ({ onFilterApply }) => {
  const { displayedFilters = {}, filterValues, setFilters } = useListContext()
  const translate = useTranslate()
  const form = useForm()
  const isLoading = require('src/helper/UseLoadingCustom').useLoadingCustom()

  useEffect(() => {
    Object.entries(filterValues).forEach((entry) => {
      const [key, value] = entry
      form.setValue(key, value)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (displayedFilters && Object.keys(displayedFilters).length === 0) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFilters])

  const onSubmit = (values) => {
    if (Object.keys(values).length > 0) {
      setFilters(values, displayedFilters, false)
      // Resetear la paginación al aplicar filtros
      if (onFilterApply) {
        onFilterApply()
      }
    }
  }

  const resetFilter = () => {
    setFilters({}, {}, false)
  }

  const areFilterActivate = (displayedFilters) => {
    if (displayedFilters && Object.keys(displayedFilters).length > 0) {
      const props = Object.values(displayedFilters)
      return props.some((prop) => prop === true)
    }
    return false
  }

  function convertToUTC(dateString) {
    return dateString
  }

  function convertFromUTC(dateString) {
    return dateString
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' mb={1} height={'auto'}>
          <Box width={'calc(100% - 156px)'} display={'flex'} flexWrap={'wrap'}>
            {displayedFilters?.external_id && (
              <RemoveFilterField prop={'external_id'} fields={['external_id']} width={300}>
                <TextInput label={translate('ra.field.operation_id')} fullWidth source='external_id' />
              </RemoveFilterField>
            )}
            {displayedFilters?.airline && (
              <RemoveFilterField prop={'airline'} fields={['airline']} width={300}>
                <SelectArrayInputFn
                  source='airline'
                  queryName='airline-all'
                  label={translate('ra.field.airline')}
                  queryFn={businessRuleServices.getAirlines}
                  optionsText={['short_name']}
                  optionValue={'short_name'}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.destination_type && (
              <RemoveFilterField prop={'destination_type'} fields={['destination_type']} width={300}>
                <SelectArrayInputFn
                  source='destination_type'
                  queryName='destination_type-all'
                  label={translate('ra.field.destination_type')}
                  queryFn={businessRuleServices.getDestinationTypes}
                  optionsText={['name']}
                  optionValue={'name'}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.currency && (
              <RemoveFilterField prop={'currency'} fields={['currency']} width={300}>
                <SelectArrayInputFn
                  source='currency'
                  queryName='currency-all'
                  label={translate('ra.field.currency')}
                  queryFn={() => [
                    { name: 'ARS', key: 'ARS' },
                    { name: 'USD', key: 'USD' }
                  ]}
                  optionsText={['name']}
                  optionValue={'key'}
                />
              </RemoveFilterField>
            )}
            {/* {displayedFilters?.destination && (
              <RemoveFilterField prop={'destination'} fields={['destination']} width={300}>
                <TextInput label={translate('ra.field.destination')} fullWidth source='destination' />
              </RemoveFilterField>
            )} */}
            {displayedFilters?.general_status && (
              <RemoveFilterField prop={'general_status'} fields={['general_status']} width={300}>
                <SelectArrayInputFn
                  source='general_status'
                  queryName='general_state-all'
                  label={translate('ra.field.general_state')}
                  queryFn={() => [
                    {
                      name: translate('ra.status.ERROR'),
                      key: 'ERROR',
                    },
                    {
                      name: translate('ra.status.ON_SITE'),
                      key: 'ON_SITE',
                    },
                    {
                      name: translate('ra.status.CANCELLED'),
                      key: 'CANCELLED',
                    },
                    {
                      name: translate('ra.status.RESERVED'),
                      key: 'RESERVED',
                    },
                    {
                      name: translate('ra.status.DELETED'),
                      key: 'DELETED',
                    }
                  ]}
                  optionsText={['name']}
                  optionValue={'key'}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.flow_status && (
              <RemoveFilterField prop={'flow_status'} fields={['flow_status']} width={500}>
                <SelectArrayInputFn
                  source='flow_status'
                  queryName='flow_status-all'
                  label={translate('ra.field.flow_status')}
                  queryFn={() => [
                    {
                      name: translate('ra.status.PREBOOKING_FAILED'),
                      key: 'PREBOOKING_FAILED',
                    },
                    // {
                    //   name: translate('ra.status.CROSSELL_SELECTED'),
                    //   key: 'CROSSELL_SELECTED',
                    // },
                    {
                      name: translate('ra.status.INIT_CHECKOUT'),
                      key: 'INIT_CHECKOUT',
                    },
                    {
                      name: translate('ra.status.FLIGHT_BOOKING_COMPLETED'),
                      key: 'FLIGHT_BOOKING_COMPLETED',
                    },
                    {
                      name: translate('ra.status.PAYMENT_IN_PROCESS'),
                      key: 'PAYMENT_IN_PROCESS',
                    },
                    {
                      name: translate('ra.status.BOOKING_REJECTED'),
                      key: 'BOOKING_REJECTED',
                    },
                    {
                      name: translate('ra.status.FRAUD'),
                      key: 'FRAUD',
                    },
                    {
                      name: translate('ra.status.SESSION_EXPIRED'),
                      key: 'SESSION_EXPIRED',
                    },
                    {
                      name: translate('ra.status.SESSION_CLOSED'),
                      key: 'SESSION_CLOSED',
                    },
                    // {
                    //   name: translate('ra.status.FRAUD_REVITION'),
                    //   key: 'FRAUD_REVITION',
                    // },
                    // {
                    //   name: translate('ra.status.CASH_PAYMENT_ABANDONED'),
                    //   key: 'CASH_PAYMENT_ABANDONED',
                    // },
                    {
                      name: translate('ra.status.PAYMENT_REJECTED'),
                      key: 'PAYMENT_REJECTED',
                    },
                    {
                      name: translate('ra.status.CREATED'),
                      key: 'CREATED',
                    },
                    // {
                    //   name: translate('ra.status.PAID_WITHOUR_FEE'),
                    //   key: 'PAID_WITHOUR_FEE',
                    // },
                    // {
                    //   name: translate('ra.status.CASH_PAYMENT'),
                    //   key: 'CASH_PAYMENT',
                    // },
                    // {
                    //   name: translate('ra.status.CONFIRMED'),
                    //   key: 'CONFIRMED',
                    // },
                    // {
                    //   name: translate('ra.status.ERROR'),
                    //   key: 'ERROR',
                    // },
                  ]}
                  optionsText={['name']}
                  optionValue={'key'}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.is_active && (
              <RemoveFilterField prop={'is_active'} fields={['is_active']} width={300}>
                <BooleanInput label={translate('ra.field.is_active')} fullWidth source='is_active' />
              </RemoveFilterField>
            )}
            {displayedFilters?.has_flexible_rate && (
              <RemoveFilterField prop={'has_flexible_rate'} fields={['has_flexible_rate']} width={300}>
                <BooleanInput label={translate('ra.field.flexi')} fullWidth source='has_flexible_rate' />
              </RemoveFilterField>
            )}
            {displayedFilters.has_ancillary_baggage && (
              <RemoveFilterField prop="has_ancillary_baggage" fields={['has_ancillary_baggage']}>
                <BooleanInput
                  source="has_ancillary_baggage"
                  label={translate('ra.title.extra_baggage')}
                  fullWidth
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.has_hepstar && (
              <RemoveFilterField prop={'has_hepstar'} fields={['has_hepstar']} width={300}>
                <BooleanInput label={'Hepstar'} fullWidth source='has_hepstar' />
              </RemoveFilterField>
            )}
            {displayedFilters?.work_unit_token && (
              <RemoveFilterField
                prop={'work_unit_token'}
                fields={['work_unit_token']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInputOperation
                  queryName='work-unit-all'
                  fieldName='work_unit_token'
                  modalTitle={translate('ra.title.select_work_unit')}
                  buttonText={translate('ra.message.filter_by_work_unit')}
                  labelField={['label']}
                  queryFn={workUnitServices.getByOrderParent}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.payment_method && (
              <RemoveFilterField prop={'payment_method'} fields={['payment_method']} width={300}>
                <SelectArrayInputFn
                  source='payment_method'
                  queryName='payment-gateway-all'
                  label={translate('ra.field.payment_method_filter')}
                  queryFn={() => gatewayServices.getByAll()}
                  optionsText={['name']}
                  optionValue={'name'}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.checkin && (
              <RemoveFilterField prop={'checkin'} fields={['checkin']} width={300}>
                <TextInput
                  label={translate('ra.field.checkin')}
                  fullWidth
                  source='checkin'
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.created_at && (
              <RemoveFilterField
                prop={'created_at'}
                fields={['datetime_from__gte', 'datetime_to__lte']}
                width={500}
              >
                <DateTimeInput
                  label={translate('ra.field.datetime_from')}
                  fullWidth
                  source='datetime_from__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.datetime_to')}
                  fullWidth
                  source='datetime_to__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.has_ancillary_hepstar && (
              <RemoveFilterField prop={'has_ancillary_hepstar'} fields={['has_ancillary_hepstar']} width={300}>
                <BooleanInput label={translate('ra.field.hepstar')} fullWidth source='has_ancillary_hepstar' />
              </RemoveFilterField>
            )}
            {displayedFilters?.has_service_package && (
              <RemoveFilterField prop={'has_service_package'} fields={['has_service_package']} width={300}>
                <BooleanInput label={translate('ra.title.servicePackage')} fullWidth source='has_service_package' />
              </RemoveFilterField>
            )}
            {displayedFilters?.promotion_label && (
              <RemoveFilterField prop={'promotion_label'} fields={['promotion_label']} width={300}>
                <PromotionFilterInput
                  source='promotion_label'
                  label={translate('ra.field.promotion')}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.booking_number && (
              <RemoveFilterField prop={'booking_number'} fields={['booking_number']} width={300}>
                <TextInput label={translate('ra.field.provider_code')} fullWidth source='booking_number' />
              </RemoveFilterField>
            )}
            {displayedFilters?.source && (
              <RemoveFilterField prop={'source'} fields={['source']} width={300}>
                <SelectArrayInputFn
                  source='source'
                  queryName='source-all'
                  label={translate('ra.field.source')}
                  queryFn={() => [
                    { name: 'AMS', key: 'AMS' },
                    { name: 'SBR', key: 'SBR' },
                    { name: 'FBO', key: 'FBO' },
                    { name: 'ARA', key: 'ARA' },
                    { name: 'NDC', key: 'NDC' }
                  ]}
                  optionsText={['name']}
                  optionValue={'key'}
                  multiple={false}
                />
              </RemoveFilterField>
            )}
          </Box>
          <Box
            width={'156px'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
          >
            {areFilterActivate(displayedFilters) && (
              <>
                <Box mr={2} paddingY={'10px'}>
                  <LoadingButton variant='outlined' color='primary' type='submit' loading={isLoading}>
                    {translate('ra.action.filter')}
                  </LoadingButton>
                </Box>
                <Box paddingY={'10px'}>
                  <Button variant='outlined' onClick={resetFilter}>
                    {translate('ra.action.close')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </form>
    </FormProvider>
  )
}
